import React from 'react'
import { RecommendedFlightBannerWrapper } from '../../RecommendedFlightBannerWrapper'
import { calculateBannerIndex } from './listingsUtils'

/**
 * Handles the PKG_DESKTOP_STATIC_BANNER_POSITION VARIANT placement logic.
 * Inserts the static cross-sell banner at position #4 and, when a dynamic
 * recommended-flight banner is also present, places it 4 slots after the static
 * banner (position #8). Mutates listingsNodes in-place.
 *
 * @param {Object} params
 * @param {Array}   params.listingsNodes                   - The mutable array of listing node objects
 * @param {string}  params.listType                        - The current list section ('listings' | 'recommendedListings')
 * @param {string}  params.relocateStaticXsellListType     - The list type that should receive the static banner
 * @param {Array}   params.recommendedListings             - Full recommended listings array (used for banner index offset)
 * @param {number}  params.dynamicBannerPlacementIndex     - Tracks how many dynamic banners have been inserted
 * @param {*}       params.xSellAd                         - The static cross-sell ad React element (may be undefined)
 * @param {boolean} params.shouldInsertRecommendedPackageDeal - Whether the dynamic flight banner should be inserted
 * @param {Object}  params.recommendedFlightBannerListing  - The recommended flight listing data
 * @param {Object}  params.recommendedFlightBannerListingProps - Props for the recommended flight banner
 * @returns {number} Updated dynamicBannerPlacementIndex
 */
export const insertDesktopStaticBannerPositionNodes = ({
  listingsNodes,
  listType,
  relocateStaticXsellListType, // eslint-disable-line no-unused-vars
  recommendedListings,
  dynamicBannerPlacementIndex,
  xSellAd,
  shouldInsertRecommendedPackageDeal,
  recommendedFlightBannerListing,
  recommendedFlightBannerListingProps,
}) => {
  // 1. Determine static placement — reuse calculateBannerIndex (handles sponsored-ad bump: index 3 → 4)
  const [, staticPlacementIndex] = calculateBannerIndex({
    listingsNodes,
    listType,
    bundleDealSortTest: false, // never append to end; always target position 4
    recommendedListings,
    dynamicBannerPlacementIndex,
  })

  // Track whether the static node was actually spliced in
  let staticWasInserted = false

  if (xSellAd !== undefined) {
    const staticBannerIndex = `${staticPlacementIndex}-${staticPlacementIndex + 1}`
    const staticXsellNode = {
      hide: false,
      id: 'pkg-top-static-xsell-position-4',
      component: React.cloneElement(xSellAd, { bannerIndex: staticBannerIndex }),
    }
    if (staticPlacementIndex >= listingsNodes.length) {
      listingsNodes.push(staticXsellNode)
    } else {
      listingsNodes.splice(staticPlacementIndex, 0, staticXsellNode)
    }
    staticWasInserted = true
  }

  // 2. Dynamic banner: 4 slots after static in the now-modified array (position #8)
  if (shouldInsertRecommendedPackageDeal) {
    // Only offset by 4 when the static splice shifted the array; otherwise land at the
    // same slot the control arm's calculateBannerIndex would select (staticPlacementIndex)
    const dynamicPlacementIndex = staticWasInserted ? staticPlacementIndex + 4 : staticPlacementIndex
    // bannerIndex mirrors calculateBannerIndex's string format → maps to creative_slot in GA4
    const bannerIndex = `${dynamicPlacementIndex}-${dynamicPlacementIndex + 1}`

    const isPrecededBySponsoredAdVariant = listingsNodes[dynamicPlacementIndex - 1]?.isSponsoredAd ?? false

    const recommendedPackageDealNode = {
      hide: false,
      afterSponsoredAd: isPrecededBySponsoredAdVariant,
      id: `pkg-banner-${recommendedFlightBannerListing.priceKey}`,
      component: (
        <RecommendedFlightBannerWrapper
          recommendedFlightBannerListing={recommendedFlightBannerListing}
          recommendedFlightBannerListingProps={recommendedFlightBannerListingProps}
          bannerIndex={bannerIndex}
        />
      ),
    }
    if (dynamicPlacementIndex >= listingsNodes.length) {
      listingsNodes.push(recommendedPackageDealNode)
    } else {
      listingsNodes.splice(dynamicPlacementIndex, 0, recommendedPackageDealNode)
    }
    dynamicBannerPlacementIndex += 1 // prevent double-insertion across list sections
  }

  return dynamicBannerPlacementIndex
}
